import axios from "axios";

import { errorToastify } from "./Toastify";
import { unprotectedUrls } from "../utils/lists";
import { logoutClearLocalStorage } from "../helpers/helperFunctions";
import { rollbar } from "../components/RollbarProvider/utils";

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  SERVER_ERROR: 500,
  CONFLICT: 409,
};
const httpErrorCodes = Object.values(HTTP_STATUS);

const handleRollbarError = (error) => {
  const status = error?.response?.status;
  const isReportableError = httpErrorCodes.includes(status);

  if (isReportableError) {
    rollbar.error("API Error", {
      status,
      url: error?.config?.url,
      method: error?.config?.method,
      data: error?.response?.data,
      message: error?.response?.data?._meta?.error?.message || error.message,
    });
  }
};

export const CustomAxios = (args = {}) => {
  const { overrideNotification = false } = args;
  const accessToken = localStorage.getItem("accessToken");

  const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_ENDPOINT,
    headers: {
      Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      "Content-Type": "application/json",
    },
  });

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const isNetworkError = error
        ?.toString()
        ?.toLowerCase()
        ?.includes("network");

      if (!isNetworkError) {
        handleRollbarError(error);
      }

      switch (error?.response?.status) {
        case HTTP_STATUS.UNAUTHORIZED: {
          let redirect = "";
          const wasUnauthorized = localStorage.getItem("unauthorized");
          logoutClearLocalStorage(null, false);

          if (!wasUnauthorized) {
            const { pathname = "", search = "" } = { ...window.location };
            redirect = `?redirect=${pathname}${search}`;
            localStorage.setItem("unauthorized", new Date().getTime());
          }

          window.location.href = `${unprotectedUrls.LOGIN}${redirect}`;
          break;
        }

        case HTTP_STATUS.FORBIDDEN: {
          if (!overrideNotification) {
            const message = error?.response?.data?._meta?.error?.message;
            errorToastify(message || "NOT AUTHORIZED");
          }
          break;
        }

        case HTTP_STATUS.SERVER_ERROR: {
          const message = "Error occurred. Kindly notify support.";

          if (!overrideNotification) {
            errorToastify(message);
          }

          if (error.response?.data?._meta?.error) {
            error.response.data._meta.error.message = message;
          }

          break;
        }

        case HTTP_STATUS.CONFLICT: {
          const message = error.response?.data?._meta?.error?.message;

          if (!overrideNotification) {
            errorToastify(
              message ||
                "Error occurred due to a conflict. Kindly notify support."
            );
          }

          break;
        }

        default: {
          if (isNetworkError) {
            errorToastify("Kindly check your connection and try again");
          } else {
            errorToastify("An unexpected error occurred.");
          }
        }
      }

      return error?.response ? Promise.reject(error) : error;
    }
  );

  return axiosInstance;
};
